import {TimeCursor, TimeCursorMode, TimeCursorType} from './time.cursor';
import {ViewerInterface} from '@/screens/ide/stringviewer/viewer.interface';
import {ViewMemento} from '@/screens/ide/stringviewer/view.memento';
import {Period} from '@/util/period';

import * as $ from "jquery";
import {BehaviorSubject, Observable} from "rxjs";
import {Project} from "@/models/project";

/**
 *
 * @class TimeCursorManager
 *
 * @brief the controller for some set of cursors on the time axis.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2021
 *
 */
export class TimeCursorManager {

  /** @var canvas HTMLCanvasElement the canvas we draw on */
  private     _canvas: HTMLCanvasElement = null;

  /** @var _viewer ViewerInterface the main viewer */
  private     _viewer: ViewerInterface   = null;

  /** @var _width number the current width of the widget */
  private      _width: number            = 0;

  /** the left offset of the canvas HTML element */
  private     _left: number              = 0;

  /** @var _height number the current height of the widget */
  private     _height: number            = 0;

  /** @var _cursors the set of cursors/indicators on the time axis */
  private _cursors: TimeCursor[]         = [];

  /** @var _dragging are we dragging and dropping a cursor? */
  private _dragging: boolean             = false;

  private _limitSubject: BehaviorSubject<TimeCursor>;
  private _limit:        Observable<TimeCursor>;

  /** standard constructor, normally only ever called by TimeAxis */
  constructor(
    id: string,
    viewer: ViewerInterface
  ) {

    const tcm: TimeCursorManager = this;

    /* setup the "time track" geometry */

    tcm._viewer        = viewer;

    tcm._canvas        = document.getElementById(id) as HTMLCanvasElement;
    tcm._width         = $('#' + id).width();
    tcm._height        = $('#' + id).height();

    tcm._left          = parseInt($(tcm._canvas).css('left'), 10);

    tcm._canvas.width  = tcm._width;
    tcm._canvas.height = tcm._height;

    /* by default we get a single time cursor, and its the one for the right side "limit" marker */

    tcm._cursors.push(new TimeCursor(TimeCursorType.LIMIT_MARKER, TimeCursorMode.OFF));

    /* watcher for hovering over a cursor */

    tcm._canvas.onmousemove = (event: MouseEvent) => {

      event.preventDefault();

      if (tcm._dragging) {

        return ;
      }

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      /* no one is hovered */

      tcm._cursors.forEach((item: TimeCursor) => {

        item._hovering = false;
      });

      /* try to find a hover */

      const  x: number = event.offsetX;
      const st: number = x + tcm._left;
      const wt: number = tcm._viewer.screenToWorld(st, 0)[0] / vm._timeScale;

      const hits: TimeCursor[] = tcm._cursors.filter(c => (c.contains(x) && (c._type == TimeCursorType.LIMIT_MARKER)));

      if (hits.length > 0) {

        hits[0]._hovering = true;
      }

      /* re-render in new mode */

      tcm.update();
    }

    tcm._canvas.onmouseout = (event: MouseEvent) => {

      /* no one is hovered */

      tcm._cursors.forEach((item: TimeCursor) => {

        item._hovering = false;
      });

      tcm.update();
    };

    /* the time track itself can be clicked on, to set a new position for the limit cursor */

    tcm._canvas.ondblclick = (event: MouseEvent) => {

      event.preventDefault();

      if (tcm._dragging) {

        return ;
      }

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      /* look for a click on the time limit cursor */

      const  x: number = event.offsetX;
      const st: number = x + tcm._left;
      const wt: number = tcm._viewer.screenToWorld(st, 0)[0] / vm._timeScale;

      const hits: TimeCursor[] = tcm._cursors.filter(c => (c.contains(x) && (c._type == TimeCursorType.LIMIT_MARKER)));

      if (hits.length == 0) {

        return ;
      }

      /* toggle/cycle mode of cursor */

      hits[0].click();

      /* re-render in new mode */

      tcm.update();

      /*
       * emit an event so that the the main viewer component can do whatever it needs to for the mode the
       * limit cursor is in now.
       *
       */

      this._limitSubject.next(hits[0]);
    }

    /* - - -  drag and drop support - - - - */

    this._canvas.onmousedown = (event:MouseEvent) => {

      event.preventDefault();

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      this.mouseDown(event.offsetX);
    };

    this._canvas.onmousedown = (event:MouseEvent) => {

      event.preventDefault();

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      this.mouseDown(event.offsetX);
    };

    this._canvas.onmousemove = (event:MouseEvent) => {

      if (!this._dragging) {

        return ;
      }

      event.preventDefault();

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      this.mouseDrag(event.offsetX);
    };

    this._canvas.onmouseup = (event:MouseEvent) => {

      if (!this._dragging) {

        return ;
      }

      event.preventDefault();

      this._dragging = false;

      let vm: ViewMemento = tcm._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      this.mouseUp(event.offsetX);
    };

    /* make sure we "emit" cursor events when the limit cursor is moved or mode toggled */

    this._limitSubject = new BehaviorSubject<TimeCursor>(null);
    this._limit        = this._limitSubject.asObservable();

    /* first time render */

    tcm.update();
  }

  /** fetch limit cursor as an Observable */
  public get limit(): Observable<TimeCursor> {

    return this._limit;
  }

  /** fetch the active project for the user..if there is one */
  public get currentLimit(): TimeCursor {

    return this._limitSubject.value;
  }

  /** @brief clear the canvas */
  clear(): void {

    this._canvas.getContext('2d').clearRect(0, 0, this._width, this._height);
  }

  /** @brief redraw all the cursors */
  update(): void {

    /* start with no cursors drawn */

    this.clear();

    const vm: ViewMemento = this._viewer.viewMemento();

    /* are we even able to draw cursors? */

    if (vm == null) {

      /* nothing to do */

      return ;
    }

    let x0     = Math.min(vm._screenGraph.max.x, vm._screenGraph.min.x);
    let x1     = Math.max(vm._screenGraph.max.x, vm._screenGraph.min.x);

    x0 -= this._left;
    x1 -= this._left;

    x0 = x0 < 0           ? 0           : x0;
    x1 = x1 > this._width ? this._width : x1;

    if ((x0  === Number.NEGATIVE_INFINITY) ||
      (x0  === Number.POSITIVE_INFINITY) ||
      isNaN(x0) ||
      (x1  === Number.NEGATIVE_INFINITY) ||
      (x1  === Number.POSITIVE_INFINITY) ||
      isNaN(x1) ||
      (x0 === x1)) {

      /* no bounds yet */

      return ;
    }

    /* if both are offscreen, we don't have to draw anything */

    if (((x0 < 0) || (x0 > this._width)) && ((x1 < 0) || (x1 > this._width))) {

      /* nothing to draw */

      return ;
    }

    const context: CanvasRenderingContext2D = this._canvas.getContext('2d');

    /* walk the cursors, updating them, and if they are on screen, draw them */

    this._cursors.forEach((cursor: TimeCursor, index: number) => {

      /* update the cursor's screen position, based on its current world position */

      const x: number = this._viewer.worldToScreen(cursor._wt * vm._timeScale, 0)[0] - this._left;

      cursor.moveTo(x, cursor._wt, Period.fromSeconds(cursor._wt * 3600).asString());

      /* only need to draw the cursors tha are actually on screen */

      const ls: number = x - cursor._width / 2.0;
      const rs: number = x + cursor._width / 2.0;

      if ((ls > x1) || (rs < x0)) {

        /* this cursor is off screen completely */

        return ;
      }

      cursor.draw(context);

    });
  }

  /** @brief respond to resizing of the axis */
  resize(): void {

    this._width         = $(this._canvas).width();
    this._height        = $(this._canvas).height();

    this._canvas.width  = this._width;
    this._canvas.height = this._height;

    this.update();
  }

  /** @brief fetch the screen width of this axis */
  width(): number {

    return this._width;
  }

  /** @brief fetch the screen height of this axis */
  height(): number {
    return this._height;
  }

  /** @brief enable dragging */
  mouseDown(x: number) {

    const hits: TimeCursor[] = this._cursors.filter(c => (c.contains(x) && (c._type == TimeCursorType.LIMIT_MARKER)));

    if (hits.length == 0) {

      /* mouse down wasn't on on the limit marker */

      this._dragging = false;

      /* look for a click on the time limit cursor */

      let vm: ViewMemento = this._viewer.viewMemento();
      let st: number      = x + this._left;
      let wt: number      = this._viewer.screenToWorld(st, 0)[0] / vm._timeScale;
      let rt: number      = vm._graph.max.x / vm._timeScale;

      if (wt < 0.0) {

        x  = 0;
        wt = 0.0;
      }

      if (wt > rt) {

        x  = 0;
        wt = rt;
      }

      /* default action outside of the limit cursor...is to move the limit cursor to here. */

      const cursor: TimeCursor = this._cursors.filter(c => c._type == TimeCursorType.LIMIT_MARKER)[0];

      cursor.moveTo(x, wt, Period.fromSeconds(wt*3600.0).asString());

      this.update();

      /*
       * emit an event so that the the main viewer component can do whatever it needs to for the mode the
       * limit cursor is in now.
       *
       */

      this._limitSubject.next(cursor);

      return ;
    }

    /* enable dragging */

    this._dragging = true;
  }

  /** show a dragging cursor */
  mouseDrag(x: number) {

    /* are we dragging? */

    if (!this._dragging) {

      return ;
    }

    /* figure out the time point on the scale */

    const vm: ViewMemento = this._viewer.viewMemento();
    let   st: number      = x + this._left;
    let   wt: number      = this._viewer.screenToWorld(st, 0)[0] / vm._timeScale;
    let   rt: number      = vm._graph.max.x / vm._timeScale;

    if (wt < 0.0) {

      x  = 0;
      st = this._left;
      wt = 0.0;

    }

    if (wt > rt) {

      x  = 0;
      wt = rt;
    }

    let drag: TimeCursor = this._cursors.find(c => c._type == TimeCursorType.LIMIT_DRAG);

    if (!drag) {

      /* we don't have a drag cursor yet, add it. */

      drag = new TimeCursor(TimeCursorType.LIMIT_DRAG, TimeCursorMode.OFF);

      this._cursors.push(drag);
    }

    /* move the drag cursor there */

    drag.moveTo(x, wt, Period.fromSeconds(wt*3600.0).asString());

    /* fresh render */

    this.update();
  }

  /** @brief drop the cursor here */
  mouseUp(x: number) {

    /* we're done dragging */

    this._dragging = false;

    /* find the drag cursor the limit cursor */

    const vm: ViewMemento = this._viewer.viewMemento();
    let drag:  TimeCursor = this._cursors.find(c => c._type == TimeCursorType.LIMIT_DRAG);
    let limit: TimeCursor = this._cursors.find(c => c._type == TimeCursorType.LIMIT_MARKER);
    let   rt: number      = vm._graph.max.x / vm._timeScale;

    if (drag) {

      if (drag._wt < 0.0) {

        drag._x    = 0;
        drag._wt   = 0.0;
        drag._text = Period.fromSeconds(drag._wt*3600.0).asString();
      }

      if (drag._wt > rt) {

        drag._x    = 0.0;
        drag._wt   = rt;
        drag._text = Period.fromSeconds(drag._wt*3600.0).asString();
      }

      /* move the limit cursor to where the drag cursor is */

      limit.moveTo(drag._x, drag._wt, drag._text);

      /* get rid of the drag cursor */

      this._cursors = this._cursors.filter(x => x._type != TimeCursorType.LIMIT_DRAG);

      /*
       * emit an event so that the the main viewer component can do whatever it needs to for the mode the
       * limit cursor is in now.
       *
       */

      this._limitSubject.next(limit);

    }

    /* fresh render without drag cursor */

    this.update();
  }
}
